/**
 * VitalState: tracking spine (Shopify theme side)
 *
 * Emits the canonical event envelope defined in VitalState-Tracking-Spine-Spec.md §4.
 * Vanilla JS, no dependencies, no jQuery.
 *
 * Responsibilities:
 *   1. Mint and persist a first-party session id that survives theme → PWA → checkout.
 *   2. Capture first-touch attribution (UTMs, gclid, fbclid, intent) once and keep it.
 *   3. Fire vs_page_view on every page and quiz_start on hero chip taps.
 *   4. Decorate every outbound link to the PWA so the session and attribution cross the hop.
 *
 * Config is injected by snippets/vs-tracking.liquid as window.VS_TRACKING_CONFIG.
 */
(function () {
  'use strict';

  var CFG = window.VS_TRACKING_CONFIG || {};
  if (CFG.enabled === false) return;

  var SESSION_KEY = 'vs_sid';
  var ATTRIBUTION_KEY = 'vs_attribution';
  // First-touch attribution is only "first touch" for a bounded window. 30 days matches
  // the credit window on the $97 → $597 upgrade so the two never disagree about a user.
  var ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

  /* ------------------------------------------------------------------ *
   * Storage helpers
   * ------------------------------------------------------------------ */

  // localStorage throws in Safari private mode rather than failing soft, and an
  // analytics script must never take the page down with it.
  function lsGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function lsSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* quota or private mode, so degrade to in-memory for this pageview */
    }
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  function setCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 864e5).toUTCString();
    var domain = CFG.cookie_domain ? '; domain=' + CFG.cookie_domain : '';
    document.cookie =
      name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/' + domain + '; SameSite=Lax';
  }

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    // RFC4122-shaped fallback for older Safari. Not cryptographically strong,
    // which is fine, because these are dedupe keys, not secrets.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  /* ------------------------------------------------------------------ *
   * Session identity
   * ------------------------------------------------------------------ */

  /**
   * Resolution order matters. An id arriving on the URL wins, because it means the
   * user is coming back from the PWA and we must not fork their identity.
   */
  function resolveSessionId() {
    var fromUrl = new URLSearchParams(window.location.search).get(SESSION_KEY);
    var id = fromUrl || getCookie(SESSION_KEY) || lsGet(SESSION_KEY) || uuid();

    setCookie(SESSION_KEY, id, 390);
    lsSet(SESSION_KEY, id);
    return id;
  }

  /**
   * GA4 writes _ga as GA1.1.<client_id_part_1>.<client_id_part_2>. The Measurement
   * Protocol needs the last two segments joined, or server events land as a separate
   * user from the browser events and every funnel double-counts.
   */
  function gaClientId() {
    var raw = getCookie('_ga');
    if (!raw) return null;
    var parts = raw.split('.');
    return parts.length >= 4 ? parts[2] + '.' + parts[3] : null;
  }

  /* ------------------------------------------------------------------ *
   * Attribution: captured once, on first touch, then frozen
   * ------------------------------------------------------------------ */

  var ATTRIBUTION_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'gclid',
    'fbclid',
    'intent'
  ];

  function currentUrlAttribution() {
    var params = new URLSearchParams(window.location.search);
    var found = {};
    var hasAny = false;

    ATTRIBUTION_PARAMS.forEach(function (key) {
      var value = params.get(key);
      if (value) {
        found[key] = value;
        hasAny = true;
      }
    });

    return hasAny ? found : null;
  }

  function resolveAttribution() {
    var stored = null;
    try {
      stored = JSON.parse(lsGet(ATTRIBUTION_KEY) || 'null');
    } catch (e) {
      stored = null;
    }

    if (stored && stored._ts && Date.now() - stored._ts > ATTRIBUTION_TTL_MS) {
      stored = null;
    }

    var fresh = currentUrlAttribution();

    // A new campaign click overwrites a stale one. Otherwise a user who converts on
    // their third campaign gets credited to the first one they ever saw, forever.
    if (fresh) {
      fresh._ts = Date.now();
      lsSet(ATTRIBUTION_KEY, JSON.stringify(fresh));
      return fresh;
    }

    return stored || {};
  }

  var SESSION_ID = resolveSessionId();
  var ATTRIBUTION = resolveAttribution();

  /* ------------------------------------------------------------------ *
   * Envelope
   * ------------------------------------------------------------------ */

  function buildEnvelope(eventName, extra) {
    extra = extra || {};

    var envelope = {
      event: eventName,
      event_id: uuid(),
      timestamp_ms: Date.now(),
      source: 'shopify_theme',
      session: {
        vs_session_id: SESSION_ID,
        ga_client_id: gaClientId()
      },
      page: {
        url: window.location.href,
        path: window.location.pathname,
        referrer: document.referrer || '',
        title: document.title || ''
      },
      attribution: {
        intent: ATTRIBUTION.intent || null,
        utm_source: ATTRIBUTION.utm_source || null,
        utm_medium: ATTRIBUTION.utm_medium || null,
        utm_campaign: ATTRIBUTION.utm_campaign || null,
        utm_content: ATTRIBUTION.utm_content || null,
        utm_term: ATTRIBUTION.utm_term || null,
        gclid: ATTRIBUTION.gclid || null,
        fbclid: ATTRIBUTION.fbclid || null
      },
      user: {},
      quiz: {},
      commerce: {}
    };

    // Shallow-merge each named sub-object so callers can pass partials.
    ['user', 'quiz', 'commerce', 'page', 'attribution'].forEach(function (key) {
      if (extra[key]) {
        Object.keys(extra[key]).forEach(function (k) {
          envelope[key][k] = extra[key][k];
        });
      }
    });

    return envelope;
  }

  /* ------------------------------------------------------------------ *
   * Dispatch
   * ------------------------------------------------------------------ */

  /**
   * GA4 params are deliberately flat. Nested objects are silently dropped by gtag,
   * which is one of the more expensive things to discover three weeks into a campaign.
   */
  function toGa4Params(envelope) {
    return {
      event_id: envelope.event_id,
      vs_session_id: envelope.session.vs_session_id,
      intent: envelope.attribution.intent || undefined,
      ir_band: envelope.quiz.ir_band || undefined,
      offer_band: envelope.quiz.offer_band || undefined,
      ir_score: typeof envelope.quiz.ir_score === 'number' ? envelope.quiz.ir_score : undefined,
      tier: envelope.commerce.tier || undefined
    };
  }

  function sendToGa4(envelope) {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', envelope.event, toGa4Params(envelope));
  }

  function sendToCollector(envelope) {
    if (!CFG.collector_url) return;

    var body = JSON.stringify(envelope);

    // sendBeacon survives the page unload that a chip tap causes. fetch with keepalive
    // is the fallback; a plain fetch would be cancelled mid-navigation and lose the event.
    if (navigator.sendBeacon) {
      var blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(CFG.collector_url, blob)) return;
    }

    try {
      fetch(CFG.collector_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        keepalive: true,
        mode: 'cors'
      }).catch(function () {});
    } catch (e) {
      /* never let a dropped event break the page */
    }
  }

  function track(eventName, extra) {
    var envelope = buildEnvelope(eventName, extra);

    sendToGa4(envelope);
    sendToCollector(envelope);

    if (CFG.debug) {
      console.log('[VS] ' + eventName, envelope);
    }

    return envelope;
  }

  /* ------------------------------------------------------------------ *
   * Cross-domain handoff
   * ------------------------------------------------------------------ */

  function isAppLink(href) {
    if (!href || !CFG.app_origins || !CFG.app_origins.length) return false;
    return CFG.app_origins.some(function (origin) {
      return href.indexOf(origin) === 0;
    });
  }

  /**
   * Rewrites a PWA link so the session id and attribution survive the domain hop.
   * Any intent already on the link (set by the merchant in the theme editor) wins over
   * stored attribution, because the chip the user just tapped is more current than history.
   */
  function decorateAppLink(href, intentOverride) {
    var url;
    try {
      url = new URL(href, window.location.origin);
    } catch (e) {
      return href;
    }

    url.searchParams.set(SESSION_KEY, SESSION_ID);

    var intent = intentOverride || url.searchParams.get('intent') || ATTRIBUTION.intent;
    if (intent) url.searchParams.set('intent', intent);

    ATTRIBUTION_PARAMS.forEach(function (key) {
      if (key === 'intent') return;
      if (ATTRIBUTION[key] && !url.searchParams.get(key)) {
        url.searchParams.set(key, ATTRIBUTION[key]);
      }
    });

    return url.toString();
  }

  function decorateAllAppLinks() {
    var anchors = document.querySelectorAll('a[href]');
    for (var i = 0; i < anchors.length; i++) {
      var href = anchors[i].getAttribute('href');
      if (isAppLink(href)) {
        anchors[i].setAttribute('href', decorateAppLink(href));
      }
    }
  }

  /* ------------------------------------------------------------------ *
   * Quiz entry points
   * ------------------------------------------------------------------ */

  /**
   * A quiz entry point is either an element explicitly marked data-vs-quiz-entry, or any
   * link pointing at the app. Auto-enrolling app links matters: the header CTA, the
   * inline CTAs and the final CTAs all start the assessment too, and if only the hero
   * chips fired quiz_start then anyone who entered via those CTAs and bounced at the
   * sign-up screen would leave no trace at all.
   */
  function findQuizTrigger(target) {
    if (!target || !target.closest) return null;

    var marked = target.closest('[data-vs-quiz-entry]');
    if (marked) return marked;

    var link = target.closest('a[href]');
    if (link && isAppLink(link.getAttribute('href'))) return link;

    return null;
  }

  /** Falls back to a readable label so GA4's entry_point is never just "unknown". */
  function entryLabel(trigger) {
    var explicit = trigger.getAttribute('data-vs-entry-label');
    if (explicit) return explicit;

    var section = trigger.closest('[id^="shopify-section-"]');
    if (section) return section.id.replace('shopify-section-', '').slice(0, 60);

    return 'app_link';
  }

  function bindQuizEntryPoints() {
    document.addEventListener(
      'click',
      function (event) {
        var trigger = findQuizTrigger(event.target);
        if (!trigger) return;

        var intent = trigger.getAttribute('data-vs-intent') || null;

        track('quiz_start', {
          attribution: intent ? { intent: intent } : {},
          quiz: { entry_point: entryLabel(trigger) }
        });

        // Re-decorate at click time rather than relying on the load-time pass: the
        // intent from this specific chip has to win, and the DOM may have changed.
        var href = trigger.getAttribute('href');
        if (href && isAppLink(href)) {
          trigger.setAttribute('href', decorateAppLink(href, intent));
        }
      },
      true // capture, so we run before any handler that calls stopPropagation
    );
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  function init() {
    decorateAllAppLinks();
    bindQuizEntryPoints();

    // Deliberately NOT called 'page_view'.
    //
    // Shopify's Google and YouTube sales channel already loads its own gtag and
    // configures it with page_view sending on. Verified on the live storefront
    // 31 Aug 2026: G-VYT5SLW0WB is present in content_for_header. Emitting a
    // second event literally named 'page_view' therefore counts every pageview
    // twice in GA4, and the duplicate is the one carrying our parameters, so it
    // cannot simply be filtered out without losing them.
    //
    // 'vs_page_view' sits alongside GA4's automatic page_view instead of
    // colliding with it. Use GA4's own page_view for traffic volume and this
    // one for anything that needs vs_session_id, intent or ir_band.
    track('vs_page_view');

    // Sections re-render on save in the theme editor; re-decorate so links stay correct.
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', decorateAllAppLinks);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public surface for other theme scripts (e.g. a future results page).
  window.VSTracking = {
    track: track,
    sessionId: SESSION_ID,
    attribution: ATTRIBUTION,
    decorateAppLink: decorateAppLink
  };
})();
